import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { Avatar, Button, HStack, Icon, Stack, Text, Wrap, WrapItem } from '@chakra-ui/react';
import { FiArrowLeft, FiCalendar, FiClock, FiDollarSign } from 'react-icons/fi';
import { format, startOfWeek, addDays, parseISO } from 'date-fns';
import { Tooltip } from '../components/Tip';
import { CalEmpty, CalError, CalLoading, CalPageHeader, CalSurface, useCalTheme } from '../components/ui';
import { BookingFact, BookingShell, BookingSteps, SelectedTimeCard, WeekSlotPicker, locationIcon, locationWords, priceWords } from './BookingParts';
import {
  PUBLIC_CALENDAR_BASIC_QUERY as PUBLIC_CALENDAR_QUERY,
  PUBLIC_EVENT_TYPE_QUERY,
  AVAILABLE_TIME_SLOTS_QUERY,
} from '../queries';

// ==================== TYPES ====================

interface PublicCalendar {
  id: string;
  name: string;
  description?: string;
  color: string;
  bookingPageSlug: string;
  bookingPageTitle?: string;
  bookingPageDescription?: string;
}

interface BookableEventType {
  id: string;
  name: string;
  description?: string;
  durationMinutes: number;
  isPaid: boolean;
  price?: number;
  currency?: string;
  locationType: string;
  location?: string;
  meetingLink?: string;
  isActive: boolean;
}

interface TimeSlot {
  startTime: string;
  endTime: string;
  available: boolean;
}

// ==================== COMPONENT ====================

/**
 * PublicBookingEventPage - Direct booking page with pre-selected event type
 *
 * This page is used when someone shares a direct link to a specific event type.
 * Example: /book/tom-miller/68abc123def456...
 *
 * It shows ONLY the calendar for the selected event type, jumping straight
 * to time selection without requiring the user to pick an event type first.
 */
export const PublicBookingEventPage: React.FC = () => {
  const { slug, eventTypeId } = useParams<{ slug: string; eventTypeId: string }>();
  const navigate = useNavigate();

  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedTime, setSelectedTime] = useState<TimeSlot | null>(null);

  const t = useCalTheme();

  // Query for calendar details
  const { data: calendarData, loading: calendarLoading, error: calendarError, refetch: refetchCalendar } = useQuery(
    PUBLIC_CALENDAR_QUERY,
    {
      variables: { slug },
      skip: !slug,
    }
  );

  // Query for event types (to find the specific event type)
  const { data: eventTypesData, loading: eventTypesLoading, error: eventTypesError, refetch: refetchEventTypes } = useQuery(
    PUBLIC_EVENT_TYPE_QUERY,
    {
      variables: { calendarSlug: slug },
      skip: !slug,
    }
  );

  // Query for available time slots
  const weekStart = startOfWeek(selectedDate, { weekStartsOn: 1 }); // Monday
  const weekEnd = addDays(weekStart, 6);

  const { data: timeSlotsData, loading: timeSlotsLoading, error: timeSlotsError, refetch: refetchTimeSlots } = useQuery(
    AVAILABLE_TIME_SLOTS_QUERY,
    {
      variables: {
        calendarId: calendarData?.publicCalendar?.id,
        eventTypeId: eventTypeId,
        startDate: weekStart.toISOString(),
        endDate: weekEnd.toISOString(),
      },
      skip: !calendarData?.publicCalendar?.id || !eventTypeId,
    }
  );

  // ==================== HANDLERS ====================

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    setSelectedTime(null);
  };

  const handleTimeSelect = (timeSlot: TimeSlot) => {
    setSelectedTime(timeSlot);
  };

  const handleBooking = () => {
    if (!eventTypeId || !selectedTime) return;

    // Navigate to booking form with selected details
    navigate(`/book/${slug}/confirm`, {
      state: {
        eventTypeId: eventTypeId,
        startTime: selectedTime.startTime,
        endTime: selectedTime.endTime,
      },
    });
  };

  // ==================== LOADING & ERROR STATES ====================

  if (calendarLoading || eventTypesLoading) {
    return (
      <BookingShell>
        <CalLoading label="Loading booking page…" cards={2} columns={{ base: 1, md: 2 }} />
      </BookingShell>
    );
  }

  // A failed request is not "not found": say which, and offer to try again.
  if (calendarError || eventTypesError) {
    return (
      <BookingShell>
        <CalError
          title="This booking page couldn't be loaded"
          error={(calendarError || eventTypesError)!}
          onRetry={() => (calendarError ? refetchCalendar() : refetchEventTypes())}
        />
      </BookingShell>
    );
  }

  if (!calendarData?.publicCalendar) {
    return (
      <BookingShell>
        <CalEmpty icon={FiCalendar} title="Booking page not found">
          This booking page doesn't exist or is no longer taking bookings. Check the link you were sent, or ask the person who shared it.
        </CalEmpty>
      </BookingShell>
    );
  }

  const calendar = calendarData.publicCalendar as PublicCalendar;
  const allEventTypes = (eventTypesData?.publicBookableEventTypes || []) as BookableEventType[];
  const eventType = allEventTypes.find((et) => et.id === eventTypeId);

  if (!eventType) {
    return (
      <BookingShell>
        <CalEmpty
          icon={FiCalendar}
          title="This option can't be booked any more"
          action={(
            <Tooltip label="See everything else you can book on this page. Nothing is booked.">
              <Button size="lg" minH="48px" leftIcon={<Icon as={FiArrowLeft} />} onClick={() => navigate(`/book/${slug}`)}>
                See all options
              </Button>
            </Tooltip>
          )}
        >
          The link you followed points to something that is no longer open for booking.
        </CalEmpty>
      </BookingShell>
    );
  }

  const timeSlots = (timeSlotsData?.availableTimeSlots || []) as TimeSlot[];

  // Group time slots by day
  const slotsByDay: Record<string, TimeSlot[]> = {};
  timeSlots.forEach((slot) => {
    const date = format(parseISO(slot.startTime), 'yyyy-MM-dd');
    if (!slotsByDay[date]) {
      slotsByDay[date] = [];
    }
    slotsByDay[date].push(slot);
  });

  // ==================== RENDER ====================

  return (
    <BookingShell maxW="container.xl">
      <Stack spacing={{ base: 4, md: 6 }}>
        <CalPageHeader
          icon={FiCalendar}
          eyebrow={calendar.bookingPageTitle || calendar.name}
          title={eventType.name}
          subtitle={eventType.description}
          back={{ to: `/book/${slug}`, label: 'All options' }}
          actions={<Avatar size="md" name={calendar.name} bg={calendar.color} color="white" />}
        />

        <CalSurface py={{ base: 3, md: 4 }}>
          <Wrap spacing={2}>
            <WrapItem><BookingFact icon={FiClock}>{eventType.durationMinutes} minutes</BookingFact></WrapItem>
            <WrapItem><BookingFact icon={FiDollarSign}>{priceWords(eventType)}</BookingFact></WrapItem>
            <WrapItem>
              <BookingFact icon={locationIcon(eventType.locationType)}>
                {locationWords(eventType.locationType)}
                {eventType.location && ` — ${eventType.location}`}
              </BookingFact>
            </WrapItem>
          </Wrap>
        </CalSurface>

        <BookingSteps done={selectedTime ? 2 : 1} />

        <WeekSlotPicker
          wide
          weekStart={weekStart}
          selectedTime={selectedTime}
          slotsByDay={slotsByDay}
          loading={timeSlotsLoading}
          error={timeSlotsError}
          onRetry={() => refetchTimeSlots()}
          onPrevWeek={() => handleDateSelect(addDays(weekStart, -7))}
          onNextWeek={() => handleDateSelect(addDays(weekStart, 7))}
          onPick={(day, slot) => {
            handleDateSelect(day);
            handleTimeSelect(slot);
          }}
        />

        {selectedTime && <SelectedTimeCard slot={selectedTime} eventType={eventType} onContinue={handleBooking} />}

        {!selectedTime && (
          <HStack spacing={2} color={t.faint} justify="center">
            <Text fontSize="md">Pick a time above to continue.</Text>
          </HStack>
        )}
      </Stack>
    </BookingShell>
  );
};

export default PublicBookingEventPage;
